import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { FormFieldComponent } from 'src/app/shared/form-field/components/form-field/form-field.component';
import { FormFieldsDirective } from 'src/app/shared/form-field/directives/form-fields/form-fields.directive';
import { FormFieldService } from 'src/app/shared/form-field/services/form-field/form-field.service';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import {
  PROFILE_FORM_EDITABLE_FIELD,
  ProfileFormField,
  profileFormValueToCreateRequestDto,
  profileFormValueToUpdateRequestDto,
  profileToFormValue,
  ProfileFormValue,
} from './profile-form.types';
import { ValidityUnit } from 'src/app/features/calist/components/create-ca/create-ca.types';
import { FormFieldOptions } from 'src/app/shared/form-field/form-field.types';
import { buildKeyUsageGroup } from 'src/app/shared/form-field/components/form-fields/key-usage-form-field/key-usage-form.types';
import { buildExtendedKeyUsageGroup } from 'src/app/shared/form-field/components/form-fields/extended-key-usage-form-field/extended-key-usage-form.types';
import { CA } from 'src/app/shared/entities/ca';
import { Profile } from 'src/app/shared/entities/profile';
import { ValidationConstants } from 'src/app/shared/validators/validation-constants';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { uniqueItemsValidator } from 'src/app/shared/validators/unique-items-validator';
import { CreateCertificateProfileRequestDto } from 'src/app/shared/services/api/dto/create-certificate-profile-request-dto';
import {
  atLeastOneCertValidity,
  maxCertValidityRequiredValidator,
} from './profile-form.validators';
import { UpdateProfileRequestDto } from 'src/app/shared/services/api/dto/update-profile-request-dto';
import { filter, merge, startWith, tap } from 'rxjs';
import { ProfileType } from 'src/app/shared/entities/profile-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
  selector: 'app-profile-form',
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    FormFieldsDirective,
    SpinnerComponent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  providers: [FormFieldService],
  templateUrl: './profile-form.component.html',
  styleUrl: './profile-form.component.scss',
})
export class ProfileFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private langService = inject(LangService);
  private dialogService = inject(DialogService);
  private formFieldService = inject(FormFieldService);

  ProfileFormField = ProfileFormField;
  ValidityUnit = ValidityUnit;

  saving = input(false);
  certificationAuthorities = input<CA[]>();
  edit = input<Profile>();
  clone = input<Profile>();

  createProfile = output<CreateCertificateProfileRequestDto>();
  updateProfile = output<UpdateProfileRequestDto>();
  cancelOperation = output<void>();

  caOptions = computed(() => {
    const certificateAuthorities = this.certificationAuthorities() ?? [];
    return certificateAuthorities.flatMap(ca => (ca.caName ? [ca.caName] : ''));
  });

  form = this.fb.group(
    {
      [ProfileFormField.profileName]: [
        '',
        [
          Validators.required,
          Validators.pattern(ValidationConstants.profile.namePattern),
        ],
      ],
      [ProfileFormField.maxCertificateValidityDays]: this.fb.control<
        number | null
      >(null, [Validators.min(0)]),
      [ProfileFormField.maxCertificateValidityMonths]: this.fb.control<
        number | null
      >(null, [Validators.min(0)]),
      [ProfileFormField.maxCertificateValidityYears]: this.fb.control<
        number | null
      >(null, [Validators.min(0)]),
      [ProfileFormField.allowExtensionOverride]: [false],
      [ProfileFormField.overridableExtensionOIDs]: this.fb.array([
        this.createOverridableExtension(),
      ]),
      [ProfileFormField.criticalBasicConstraints]: [false],
      [ProfileFormField.keyUsages]: buildKeyUsageGroup(this.fb),
      [ProfileFormField.extendedKeyUsages]: buildExtendedKeyUsageGroup(this.fb),
      [ProfileFormField.crlDistributionEndpoint]: [
        '',
        [Validators.pattern(ValidationConstants.urlRegExp)],
      ],
      [ProfileFormField.useLastToFirstDnOrder]: [false],
      [ProfileFormField.ca]: ['', [Validators.required]],
      [ProfileFormField.pathLengthConstraint]: this.fb.control<number | null>(
        null,
        [
          Validators.min(0),
          Validators.max(ValidationConstants.profile.pathLengthConstraint.max),
        ],
      ),
      [ProfileFormField.profileType]: ['', [Validators.required]],
      [ProfileFormField.criticalCRLDistributionPoint]: [false],
      [ProfileFormField.ocspServiceLocator]: [
        '',
        [Validators.pattern(ValidationConstants.urlRegExp)],
      ],
    },
    {
      validators: [atLeastOneCertValidity, maxCertValidityRequiredValidator],
    },
  );
  labels = this.langService.lang.features.profileForm;
  onRemoveOids(index: number) {
    this.form.controls[ProfileFormField.overridableExtensionOIDs].removeAt(
      index,
    );
  }

  onAddOids() {
    this.form.controls[ProfileFormField.overridableExtensionOIDs].push(
      this.createOverridableExtension(),
    );
  }

  title = computed(() =>
    this.edit() ? this.labels.editTitle : this.labels.createTitle,
  );

  onCancel() {
    this.dialogService.withConfirmation({
      text: this.labels.confirmCancel,
      confirmIf: () => this.form.dirty,
    })(() => {
      this.cancelOperation.emit(undefined);
    });
  }

  allZerosErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: (control, form) =>
      !!(control?.touched && control?.errors) ||
      (control?.touched && form?.hasError('maxCertValidityRequired')) ||
      form?.hasError('allZero') ||
      false,
  };

  onCreate() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (this.form.valid) {
      const dto = profileFormValueToCreateRequestDto(this.form.value);
      this.createProfile.emit(dto);
    }
  }

  onUpdate() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (this.form.valid) {
      const dto = profileFormValueToUpdateRequestDto(this.form.value);
      this.updateProfile.emit(dto);
    }
  }

  private createOverridableExtension() {
    return this.fb.control('', [uniqueItemsValidator]);
  }

  // Flag protecting CA from reacting to changes of profile type
  private isValueBeingCopied = false;

  private copyFormValueToForm(formValue: ProfileFormValue) {
    // If edited profile has no overridable extensions, let's assume that
    // ProfileFormField.overridableExtensionOIDs array has a single empty item,
    // as created by default.
    // If edited profile has overridable extensions, make sure the
    // ProfileFormField.overridableExtensionOIDs array has as many items as
    // there are extensions.
    this.isValueBeingCopied = true;
    const nOverridableExtensionOIDs = Math.max(
      formValue[ProfileFormField.overridableExtensionOIDs]?.length ?? 1,
      1,
    );
    if (nOverridableExtensionOIDs > 1) {
      for (let i = 0; i < nOverridableExtensionOIDs - 1; ++i) {
        this.form.controls[ProfileFormField.overridableExtensionOIDs].push(
          this.createOverridableExtension(),
        );
      }
    }
    // For the autocomplete to catch up with the patched value, patching is
    // deferred to the next tick.
    setTimeout(() => {
      this.form.patchValue(formValue);
      this.isValueBeingCopied = false;
    });
  }

  ngOnInit() {
    const editedProfile = this.edit();
    const isEditing = !!editedProfile;
    const clonedProfile = this.clone();
    const isCloning = !!clonedProfile;
    if (isEditing) {
      const formValue = profileToFormValue(editedProfile);
      this.copyFormValueToForm(formValue);
      const editableFields = new Set<string>(PROFILE_FORM_EDITABLE_FIELD);
      Object.entries(this.form.controls).forEach(([formField, control]) => {
        if (!editableFields.has(formField)) {
          control.disable();
        }
      });
    }
    if (isCloning) {
      // Prefill all fields except Profile Name
      const formValue = {
        ...profileToFormValue(clonedProfile),
        [ProfileFormField.profileName]: null,
      };
      this.copyFormValueToForm(formValue);
      const caNameEnabled =
        clonedProfile.profileType === ProfileType.END_ENTITY;
      const caNameControl = this.form.controls[ProfileFormField.ca];
      if (caNameEnabled) {
        caNameControl.enable();
      } else {
        caNameControl.disable();
      }
    }
    this.formFieldService.configure({
      labels: this.labels.inputs,
      options: {
        [ProfileFormField.profileName]: FormFieldOptions.text({
          required: true,
        }),
        [ProfileFormField.profileType]: FormFieldOptions.select({
          required: true,
        }),
        [ProfileFormField.maxCertificateValidityDays]: FormFieldOptions.text({
          inputType: 'number',
          min: 0,
        }),
        [ProfileFormField.maxCertificateValidityMonths]: FormFieldOptions.text({
          inputType: 'number',
          min: 0,
        }),
        [ProfileFormField.maxCertificateValidityYears]: FormFieldOptions.text({
          inputType: 'number',
          min: 0,
        }),
        [ProfileFormField.allowExtensionOverride]: FormFieldOptions.checkbox(),
        [ProfileFormField.criticalBasicConstraints]:
          FormFieldOptions.checkbox(),
        [ProfileFormField.keyUsages]: FormFieldOptions.keyUsages(),
        [ProfileFormField.extendedKeyUsages]:
          FormFieldOptions.extendedKeyUsages(),
        [ProfileFormField.useLastToFirstDnOrder]: FormFieldOptions.checkbox(),
        [ProfileFormField.ca]: FormFieldOptions.autocomplete(),
        [ProfileFormField.pathLengthConstraint]: FormFieldOptions.text({
          inputType: 'number',
          min: 0,
          max: ValidationConstants.profile.pathLengthConstraint.max,
        }),
        [ProfileFormField.crlDistributionEndpoint]: FormFieldOptions.text({}),
        [ProfileFormField.criticalCRLDistributionPoint]:
          FormFieldOptions.checkbox(),
        [ProfileFormField.ocspServiceLocator]: FormFieldOptions.text({}),
      },
    });
  }

  private disableCaUnlessProfileTypeIsEndEntity = this.form.controls[
    ProfileFormField.profileType
  ].valueChanges.pipe(
    startWith(null),
    // Creation or clone mode: always do this
    // Edit mode: only do this if ProfileFormField.ca is editable
    filter(
      () =>
        !this.edit() ||
        PROFILE_FORM_EDITABLE_FIELD.includes(ProfileFormField.ca),
    ),
    filter(() => !this.isValueBeingCopied),
    tap(value => {
      const caNameControl = this.form.controls[ProfileFormField.ca];
      const caNameEnabled = value === ProfileType.END_ENTITY;
      caNameControl.setValue('', {
        emitEvent: false,
      });
      if (caNameEnabled) {
        caNameControl.enable();
      } else {
        caNameControl.disable();
      }
    }),
  );

  private enableOrDisableCaForEditEffect = () => {
    if (!PROFILE_FORM_EDITABLE_FIELD.includes(ProfileFormField.ca)) return;
    const profile = this.edit();
    if (profile) {
      const caNameControl = this.form.controls[ProfileFormField.ca];
      const caNameEnabled = profile.profileType === ProfileType.END_ENTITY;
      if (caNameEnabled) {
        caNameControl.enable();
      } else {
        caNameControl.disable();
      }
    }
  };

  constructor() {
    merge(this.disableCaUnlessProfileTypeIsEndEntity)
      .pipe(takeUntilDestroyed())
      .subscribe();
    effect(this.enableOrDisableCaForEditEffect);
  }
}
